'use client';

import { useAllBalances } from '@frontend/client/hooks/useUserBalances';
import { normalizeBigInt, truncateAddress } from '@frontend/common';
import PrivacyAmount from '@frontend/components/PrivacyAmount';
import { usePrimaryName } from '@justaname.id/react';
import { Card, Flex, Skeleton, Text } from '@radix-ui/themes';
import { FC, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';
import type { Address } from 'viem';

interface WalletListItemProps {
  address?: Address;
  label: string;
  isCurrent: boolean;
  onSwitch: () => Promise<void>;
}

const WalletListItem: FC<WalletListItemProps> = ({
  address,
  label,
  isCurrent,
  onSwitch,
}) => {
  const { primaryName } = usePrimaryName({ address });
  const { allBalances, isLoading } = useAllBalances({ address });

  const totalUsd = useMemo(() => {
    const total =
      allBalances?.reduce((sum, b) => sum + (b.valueUSD || 0n), 0n) || 0n;
    return normalizeBigInt(total, 6);
  }, [allBalances]);

  return (
    <Card
      variant="surface"
      size="1"
      className={`py-2 transition-all duration-300 ${
        !isCurrent ? 'cursor-pointer' : ''
      }`}
      onClick={() => {
        if (!isCurrent) onSwitch();
      }}
      role={!isCurrent ? 'button' : undefined}
      tabIndex={!isCurrent ? 0 : -1}
      aria-disabled={isCurrent}
      onKeyDown={(e) => {
        if (isCurrent) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSwitch();
        }
      }}
    >
      <Flex justify="between" align="center" className="w-full gap-3">
        <Flex direction="column" className="min-w-0 leading-tight">
          <Text size="2" weight="bold" className="truncate">
            {primaryName || truncateAddress(address || '', 4)}
          </Text>
          <Text size="1" color="gray" className="truncate">
            {label}
          </Text>
          {isLoading ? (
            <Skeleton width="64px" height="12px" />
          ) : (
            <PrivacyAmount size="1" color="gray">
              <NumericFormat
                value={totalUsd || 0}
                displayType="text"
                thousandSeparator
                prefix="$"
                decimalScale={2}
                fixedDecimalScale
              />
            </PrivacyAmount>
          )}
        </Flex>
        <Flex gap="2" align="center" className="shrink-0">
          {isCurrent ? (
            <Flex align="center" gap="1">
              <Text as="span" size="1" color="green" className="leading-none">
                Current
              </Text>
            </Flex>
          ) : (
            <Text
              as="span"
              size="1"
              className="leading-none text-[--accent-11]"
            >
              Switch
            </Text>
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

export default WalletListItem;
